import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../data/base';

/**
 * SERVIZIO GENERAZIONE CSV CATALOGO
 * =================================
 * 
 * Questo servizio genera un file CSV contenente tutti i dati delle varianti del catalogo.
 * 
 * LOGICA DI FUNZIONAMENTO:
 * 1. PREPARAZIONE: Crea header CSV
 * 2. RECUPERO WEBDOMAIN: Query collezione "Catalogues" → trova doc con doc.id = catalogueId → estrae "webDomain"
 * 3. RECUPERO VIEWER ID: Query collezione "3DViewers" → trova doc con proprietà "catalog" = catalogueId → estrae "doc.id"
 * 4. CONTA VARIANTI: Calcola totale varianti per barra progresso
 * 5. ITERAZIONE: Per ogni prodotto → per ogni variante
 * 6. ESTRAZIONE DATI: Recupera dati dal documento CataloguesVariants
 * 7. QUERY FIREBASE: Cerca il documento nella collezione "CataloguesVariants" usando l'ID
 * 8. COSTRUZIONE LINK: Genera VTO (con webDomain), 3D Single e 3D Multiple (con viewerId)
 * 9. PROGRESSO: Aggiorna la barra di progresso ad ogni variante
 * 10. FINALIZZAZIONE: Restituisce il CSV completo
 * 
 * STRUTTURA CSV GENERATA:
 * - SKU CODE: Codice identificativo (da CataloguesVariants.skuModel)
 * - EAN CODE: Codice EAN (da CataloguesVariants.eanCode)
 * - UPC CODE: Codice UPC (da CataloguesVariants.upcCode)
 * - MODEL NAME: Nome modello (da CataloguesVariants.glassesName)
 * - VTO LINK: https://{webDomain}/?c={catalogueId}&m={CataloguesVariants.doc.id} (webDomain da Catalogues[catalogueId].webDomain)
 * - 3DViewer Single Link: https://{REACT_APP_ACTIVE_BASE_URL}/Arshades3d/{3DViewers.doc.id}/glasses/{catalogueProductRef}/variant/{idCataloguesVariants}
 * - 3DViewer Multiple Link: https://{REACT_APP_ACTIVE_BASE_URL}/Arshades3ds/{3DViewers.doc.id}/glasses/{catalogueProductRef}
 */

// Test fatto su BOSS EYEWEAR 45 SKU e su CARRERA 992 SKU

export const generateCatalogueCSV = async (allProducts, variants, catalogueId, onProgress) => {
    try {
        // ===============================
        // FASE 1: PREPARAZIONE CSV + RECUPERO WEBDOMAIN E BASE URL
        // ===============================
        const headers = [
            'SKU CODE',
            'EAN CODE',
            'UPC CODE',
            'MODEL NAME',
            'VTO LINK',
            '3DViewer Single Link',
            '3DViewer Multiple Link'
        ];

        const csvRows = [headers.join(',')];

        // Recupera la base URL dall'ambiente (senza https://)
        const baseUrl = process.env.REACT_APP_ACTIVE_BASE_URL
            ? process.env.REACT_APP_ACTIVE_BASE_URL.replace(/^https?:\/\//, '')
            : 'arshadesstaging.web.app'; // Fallback di default

        // Recupera il webDomain dalla collezione Catalogues
        let webDomain = 'www.webvto.it'; // Fallback di default
        try {
            const catalogueRef = doc(db, "Catalogues", catalogueId);
            const catalogueSnap = await getDoc(catalogueRef);
            if (catalogueSnap.exists()) {
                const catalogueData = catalogueSnap.data();
                if (catalogueData.webDomain) {
                    webDomain = catalogueData.webDomain;
                    // Assicurati che non inizi con http:// o https://
                    if (webDomain.startsWith('http://') || webDomain.startsWith('https://')) {
                        webDomain = webDomain.replace(/^https?:\/\//, '');
                    }
                    // Aggiungi www. se non presente
                    if (!webDomain.startsWith('www.')) {
                        webDomain = `www.${webDomain}`;
                    }
                }
            }
        } catch (error) {
            console.warn('Errore nel recuperare webDomain dal catalogo, uso fallback:', error);
        }

        // Recupera l'ID del 3D Viewer dalla collezione 3DViewers
        let viewerId = catalogueId; // Fallback di default
        try {
            const viewerDoc = await findDocumentByField('3DViewers', 'catalog', catalogueId);
            if (viewerDoc) {
                viewerId = viewerDoc.id; // Usa il doc.id del documento 3DViewers
            }
        } catch (error) {
            console.warn('Errore nel recuperare viewerId da 3DViewers, uso fallback:', error);
        }

        // Conta il totale delle varianti per la barra di progresso
        let totalVariants = 0;
        allProducts.forEach(product => {
            if (variants[product.id]) {
                totalVariants += variants[product.id].length;
            }
        });

        let processedVariants = 0;

        // Caso edge: nessuna variante trovata
        if (totalVariants === 0) {
            onProgress(0, 0);
            return csvRows.join('\n');
        }

        // ===============================
        // FASE 2: ITERAZIONE PRODOTTI/VARIANTI
        // ===============================
        for (const product of allProducts) {
            // Salta prodotti senza varianti
            if (!variants[product.id] || variants[product.id].length === 0) {
                continue;
            }

            // Processa ogni variante del prodotto
            for (const variant of variants[product.id]) {
                try {
                    // Aggiorna progresso UI in tempo reale
                    onProgress(processedVariants, totalVariants);

                    // ===============================
                    // FASE 3: ESTRAZIONE DATI DA CATALOGUESVARIANTS
                    // ===============================

                    // Prende l'ID della variante (es. 'C3I8dgJ2uMGqCkQG9Iur')
                    const variantId = variant.id;

                    // Inizializza i valori di default
                    let sku = '';
                    let eanCode = '';
                    let upcCode = '';
                    let modelName = '';
                    let mainBrandRef = '';
                    let modelViewerUrl = '';
                    let catalogProductRef = '';

                    if (variantId) {
                        try {
                            // Query Firebase: recupera il documento dalla collezione "CataloguesVariants"
                            const catalogueVariantRef = doc(db, "CataloguesVariants", variantId);
                            const catalogueVariantSnap = await getDoc(catalogueVariantRef);

                            if (catalogueVariantSnap.exists()) {
                                // Documento trovato: estrae tutti i dati necessari
                                const catalogueVariantData = catalogueVariantSnap.data();

                                // COLONNA 1: SKU CODE
                                sku = catalogueVariantData.skuModel || '';

                                // COLONNA 2: EAN CODE
                                eanCode = catalogueVariantData.eanCode || '';

                                // COLONNA 3: UPC CODE
                                upcCode = catalogueVariantData.upcCode || '';

                                // COLONNA 4: MODEL NAME
                                modelName = catalogueVariantData.glassesName || '';

                                // Per le colonne 5, 6, 7: salva catalogueProductRef per costruire i link dinamicamente
                                mainBrandRef = catalogueVariantData.mainBrandRef || ''; // Mantenuto per compatibilità
                                modelViewerUrl = catalogueVariantData.modelViewerUrl || ''; // Mantenuto per fallback
                                catalogProductRef = catalogueVariantData.catalogueProductRef || catalogueVariantData.catalogProductRef || '';
                            } else {
                                // Documento non trovato: usa fallback dai dati locali
                                console.warn(`Documento CataloguesVariants non trovato per ID: ${variantId}`);
                                sku = variant.skuModel || variant.sku || '';
                                eanCode = variant.eanCode || '';
                                upcCode = variant.upcCode || '';
                                modelName = variant.glassesName || '';
                                mainBrandRef = product.mainBrandRef || '';
                                modelViewerUrl = variant.modelViewerUrl || '';
                                catalogProductRef = variant.catalogueProductRef || variant.catalogProductRef || variant.productRef || '';
                            }
                        } catch (error) {
                            // Errore Firebase: usa fallback dai dati locali
                            console.warn(`Errore nel recuperare CataloguesVariants per ID ${variantId}:`, error);
                            sku = variant.skuModel || variant.sku || '';
                            eanCode = variant.eanCode || '';
                            upcCode = variant.upcCode || '';
                            modelName = variant.glassesName || '';
                            mainBrandRef = product.mainBrandRef || '';
                            modelViewerUrl = variant.modelViewerUrl || '';
                            catalogProductRef = variant.catalogueProductRef || variant.catalogProductRef || variant.productRef || '';
                        }
                    }

                    // ===============================
                    // FASE 4: COSTRUZIONE LINK
                    // ===============================

                    // COLONNA 5: VTO LINK (Virtual Try-On)
                    // Formato: https://{webDomain}/?c={catalogueId}&m={variantId}
                    let linkVTO = '';
                    if (catalogueId && variantId) {
                        linkVTO = `https://${webDomain}/?c=${catalogueId}&m=${variantId}`;
                    }

                    // COLONNA 6: 3DViewer Single Link
                    // Costruisce dinamicamente: https://{baseUrl}/Arshades3d/{viewerId}/glasses/{catalogueProductRef}/variant/{idCataloguesVariants}
                    let link3DSingle = '';
                    if (viewerId && catalogProductRef && variantId) {
                        link3DSingle = `https://${baseUrl}/Arshades3d/${viewerId}/glasses/${catalogProductRef}/variant/${variantId}`;
                    } else if (modelViewerUrl) {
                        // Fallback: usa modelViewerUrl originale (rimuove @ iniziale se presente)
                        link3DSingle = modelViewerUrl.startsWith('@')
                            ? modelViewerUrl.substring(1)
                            : modelViewerUrl;
                    }

                    // COLONNA 7: 3DViewer Multiple Link
                    // Costruisce dinamicamente: https://{baseUrl}/Arshades3ds/{viewerId}/glasses/{catalogueProductRef}
                    let link3DMultiple = '';
                    if (viewerId && catalogProductRef) {
                        link3DMultiple = `https://${baseUrl}/Arshades3ds/${viewerId}/glasses/${catalogProductRef}`;
                    } else if (link3DSingle) {
                        // Fallback: modifica il link 3D single (Arshades3d → Arshades3ds + rimuove /variant/)
                        link3DMultiple = link3DSingle
                            .replace('Arshades3d', 'Arshades3ds')
                            .replace(/\/variant\/[^\/]+$/, '');
                    }

                    // ===============================
                    // FASE 5: CREAZIONE RIGA CSV
                    // ===============================
                    const row = [
                        escapeCSVField(sku),
                        escapeCSVField(eanCode),
                        escapeCSVField(upcCode),
                        escapeCSVField(modelName),
                        escapeCSVField(linkVTO),
                        escapeCSVField(link3DSingle),
                        escapeCSVField(link3DMultiple)
                    ];

                    csvRows.push(row.join(','));
                    processedVariants++;

                    // Pausa ogni 5 varianti per aggiornare l'UI
                    if (processedVariants % 5 === 0) {
                        await new Promise(resolve => setTimeout(resolve, 10));
                    }

                } catch (error) {
                    // Errore su singola variante: continua con le altre
                    console.error(`Errore nel processare la variante ${variant.id}:`, error);
                    processedVariants++;
                }
            }
        }

        // ===============================
        // FASE 6: FINALIZZAZIONE
        // ===============================
        onProgress(processedVariants, totalVariants);
        return csvRows.join('\n');

    } catch (error) {
        console.error('Errore nella generazione del CSV:', error);
        throw new Error('Errore durante la generazione del CSV');
    }
};

/**
 * FUNZIONE UTILITY: Escape dei campi CSV
 * ======================================
 * 
 * Gestisce caratteri speciali nei campi CSV (virgole, virgolette, a capo).
 * Segue lo standard RFC 4180 per il formato CSV.
 * 
 * LOGICA:
 * - Se il campo contiene caratteri speciali → racchiude tra virgolette
 * - Raddoppia le virgolette esistenti per l'escape
 * - Gestisce valori null/undefined → stringa vuota
 */
const escapeCSVField = (field) => {
    if (field === null || field === undefined) {
        return '';
    }

    const stringField = String(field);

    // Controlla se servono le virgolette di escape
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
        // Raddoppia le virgolette e racchiude tutto tra virgolette
        return `"${stringField.replace(/"/g, '""')}"`;
    }

    return stringField;
};

/**
 * FUNZIONE UTILITY: Ricerca documento Firebase
 * ============================================
 * 
 * Cerca un documento in una collezione Firebase usando un campo specifico.
 * Funzione generica riutilizzabile per query semplici.
 * 
 * PARAMETRI:
 * - collectionName: Nome della collezione Firebase
 * - field: Campo su cui filtrare
 * - value: Valore da cercare
 * 
 * RITORNA:
 * - Oggetto documento (con id) se trovato
 * - null se non trovato o errore
 */
export const findDocumentByField = async (collectionName, field, value) => {
    try {
        const q = query(
            collection(db, collectionName),
            where(field, "==", value)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return { id: doc.id, ...doc.data() };
        }

        return null;
    } catch (error) {
        console.error(`Errore nel cercare documento in ${collectionName}:`, error);
        return null;
    }
}; 